import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Input } from '@/components/ui-kit/input';
import { useToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { Plus } from 'lucide-react';
import { CHUNKING_STRATEGIES } from '@/modules/gpt-chats/constants';
import { useCreateFolder } from '@/modules/gpt-chats/hooks/use-kb';
import { ICreateFolderPayload } from '@/modules/gpt-chats/types/kb.service.type';
import { useGetLlmModels } from '@/modules/gpt-chats/hooks/use-agents';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import ModelSelection from '../../../model-selection-internal/model-selection';

const getAddFolderSchema = (t: TFunction) =>
  z.object({
    name: z.string().trim().min(1, t('NAME_IS_REQUIRED')).max(50, t('NAME_MAX_LENGTH')),
    description: z
      .string()
      .trim()
      .min(1, t('DESCRIPTION_IS_REQUIRED'))
      .max(200, t('DESCRIPTION_MAX_LENGTH')),
    llm_config: z.object({
      model_name: z.string().trim(),
      is_blocks_provider: z.boolean(),
      provider: z.string().trim(),
      model_id: z.string().trim().optional(),
    }),
    chunking_strategy: z.string().trim().min(1, t('CHUNKING_STRATEGY_IS_REQUIRED')),
  });

type AddFolderForm = z.infer<ReturnType<typeof getAddFolderSchema>>;

export const AddFolderModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const createFolder = useCreateFolder();
  const isPending = createFolder.isPending;

  const { data: blocksModelsData } = useGetLlmModels();

  const form = useForm<AddFolderForm>({
    resolver: zodResolver(getAddFolderSchema(t)),
    defaultValues: {
      name: '',
      description: '',
      llm_config: {
        model_name: '',
        is_blocks_provider: true,
        provider: '',
      },
      chunking_strategy: 'recursive',
    },
  });

  useEffect(() => {
    if (!blocksModelsData) return;
    const currentModelName = form.getValues('llm_config.model_name');
    if (currentModelName) return;

    const embeddingModels = blocksModelsData.filter((m) => m.model_type === 'embedding');
    const preferredModel = embeddingModels.find((m) => m.model_name === 'text-embedding-ada-002');
    const firstEmbeddingModel = preferredModel || embeddingModels[0];
    if (firstEmbeddingModel) {
      form.setValue('llm_config.model_name', firstEmbeddingModel.model_name, {
        shouldValidate: false,
      });
      form.setValue('llm_config.is_blocks_provider', true, { shouldValidate: false });
      form.setValue('llm_config.provider', firstEmbeddingModel.provider, { shouldValidate: false });
    }
  }, [blocksModelsData, form]);

  const onSubmit = (values: AddFolderForm) => {
    const payload: ICreateFolderPayload = {
      name: values.name,
      description: values.description,
      embedding_model_config: {
        Provider: values.llm_config.provider,
        Model: values.llm_config.model_name,
        ChunkingStrategy: values.chunking_strategy,
        IsProviderBlocks: values.llm_config.is_blocks_provider,
        ModelId: values.llm_config.model_id as string,
      },
    };

    createFolder.mutate(payload, {
      onSuccess: () => {
        toast({ description: t('FOLDER_ADDED_SUCCESSFULLY'), variant: 'success' });
        setOpen(false);
        form.reset();
      },
      onError: (error) => {
        toast.error({ errors: ErrorTransformer(error) });
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          {t('ADD_FOLDER')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t('ADD_FOLDER')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('NAME')}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={t('ENTER_FOLDER_NAME')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('DESCRIPTION')}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={t('ENTER_DESCRIPTION')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ModelSelection form={form} modelType="embedding" isRequired={true} />

              <FormField
                control={form.control}
                name="chunking_strategy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('CHUNKING_STRATEGY')}</FormLabel>
                    <FormControl>
                      <Select onValueChange={(value) => field.onChange(value)} value={field.value}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('SELECT_STRATEGY')} />
                        </SelectTrigger>
                        <SelectContent>
                          {CHUNKING_STRATEGIES.map((strategy) => (
                            <SelectItem key={strategy.value} value={strategy.value}>
                              {strategy.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  form.reset();
                }}
                disabled={isPending}
              >
                {t('CANCEL')}
              </Button>
              <Button
                type="submit"
                disabled={isPending || !form.watch('name') || !form.watch('description')}
              >
                {isPending ? t('ADDING') : t('ADD_FOLDER')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
